import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { AuthContext } from '../../AuthContext';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import "../../assets/CSS/Dashboard.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const IMAGE_BASE_URL = process.env.REACT_APP_API_BASE_PATH || BASE_URL;

const getPhotoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (!url.startsWith('/uploads')) {
    return `${IMAGE_BASE_URL}/uploads${url.startsWith('/') ? url : '/' + url}`;
  }
  return `${IMAGE_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
};

const ReportTable = ({ title, description, icon, color = '#2563eb', headers, rows }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', background: '#fff', height: '100%' }}>
    <div style={{ padding: '16px 18px', borderBottom: '1px solid #eef2f7', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: `${color}18`, color, display: 'grid', placeItems: 'center', flexShrink: 0 }}><i className={`bi ${icon}`}></i></div>
      <div><div style={{ fontWeight: 800, color: '#172554' }}>{title}</div><div className="text-muted" style={{ fontSize: '12px', marginTop: 2 }}>{description}</div></div>
    </div>
    <div className="table-responsive"><table className="table table-sm mb-0 align-middle report-table"><thead><tr>{headers.map(header => <th key={header} style={{ background: '#252525', color: '#fff', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.3px', padding: '11px 14px', borderBottom: '1px solid #e2e8f0' }}>{header}</th>)}</tr></thead><tbody>{rows.length ? rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} style={{ padding: '12px 14px', color: '#334155', fontWeight: cellIndex === 0 ? 700 : 500 }}>{cell}</td>)}</tr>) : <tr><td colSpan={headers.length} className="text-center text-muted py-4">Abhi koi record nahi hai.</td></tr>}</tbody></table></div>
  </div>
);

const readableTicketStatus = (status) => ({
  OPEN: 'Pending action', TEMPORARY: 'Temporary ticket', ACKNOWLEDGED: 'Acknowledged',
  READY_FOR_VERIFICATION: 'Check & close', PERMANENT: 'Permanent incident',
  REMEDIATION_ASSIGNED: 'Recovery worker assigned', REMEDIATION_VERIFIED: 'Recovery verified'
}[status] || status || '-');

export default function ManagerRemediation() {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [workflowReport, setWorkflowReport] = useState({ ageing: [], defects: [], repeat_failures: [], recovery: [], false_tickets: [] });
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState('live');

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [escalationStatusFilter, setEscalationStatusFilter] = useState('ALL');
  const [escalationSearch, setEscalationSearch] = useState('');
  const [escalationView, setEscalationView] = useState('grid');
  const [escalationPage, setEscalationPage] = useState(1);

  // Remediation Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [workersList, setWorkersList] = useState([]);
  const [workerSearch, setWorkerSearch] = useState('');
  const [assignSupervisor, setAssignSupervisor] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [assignWorker, setAssignWorker] = useState('');
  const [assignQty, setAssignQty] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [standardTime, setStandardTime] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [verifier, setVerifier] = useState('QC');
  const [assigning, setAssigning] = useState(false);
  const [submittingManpower, setSubmittingManpower] = useState(false);

  // Manpower State
  const [activeProcesses, setActiveProcesses] = useState([]);
  const [mpHistory, setMpHistory] = useState([]);
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [mpHistoryFilterText, setMpHistoryFilterText] = useState('');
  const [trainingHistoryFilterText, setTrainingHistoryFilterText] = useState('');
  const [mpModalType, setMpModalType] = useState(null);
  const [mpRedProcess, setMpRedProcess] = useState('');
  const [mpSourceProcess, setMpSourceProcess] = useState('');
  const [mpAvailableWorkers, setMpAvailableWorkers] = useState([]);
  const [mpSelectedWorker, setMpSelectedWorker] = useState('');
  const [trainingSkill, setTrainingSkill] = useState('');
  const [trainingNotes, setTrainingNotes] = useState('');
  const [trainingStartAt, setTrainingStartAt] = useState('');
  const [trainingEndAt, setTrainingEndAt] = useState('');
  const [trainingTestResult, setTrainingTestResult] = useState('');
  const [trainingApprover, setTrainingApprover] = useState('');
  const [otStartAt, setOtStartAt] = useState('');
  const [otEndAt, setOtEndAt] = useState('');
  const [otHours, setOtHours] = useState('');
  const [lendingSupervisor, setLendingSupervisor] = useState('');
  const [expectedCapacity, setExpectedCapacity] = useState('');
  const [manpowerNotes, setManpowerNotes] = useState('');

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/overview`, {
        params: {
          role: user?.Auth,
          department: user?.Department,
          supervisorName: user?.Name
        }
      });
      if (res.data.success) {
        setTasks(res.data.tasks);
        setTickets(res.data.escalated_tickets);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch manager data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveProcesses = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/active-processes`);
      if (res.data.success) {
        setActiveProcesses(res.data.processes);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchManpowerHistory = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/manpower/history`);
      if (res.data.success) {
        setMpHistory(res.data.history);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTrainingHistory = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/training/history`);
      if (res.data.success) setTrainingHistory(res.data.training || []);
    } catch (err) { console.error('Training history error:', err); }
  };

  const fetchWorkflowReport = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/admin/workflow-report`);
      if (res.data.success) setWorkflowReport(res.data);
    } catch (err) { console.error('Workflow report error:', err); }
  };

  useEffect(() => {
    if (!user) { window.location.href = "/"; return; }
    fetchData();
    fetchActiveProcesses();
    fetchManpowerHistory();
    fetchTrainingHistory();
    fetchWorkflowReport();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const fetchWorkersForSupervisor = async () => {
      if (!assignSupervisor) {
        setWorkersList([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/workers-list`, {
          params: { supervisorName: assignSupervisor }
        });
        if (res.data.success) {
          setWorkersList(res.data.workers);
        }
      } catch (err) {
        console.error('Fetch workers error:', err);
        setWorkersList([]);
      }
    };
    fetchWorkersForSupervisor();
  }, [assignSupervisor]);

  const openRemediationModal = async (ticket) => {
    setSelectedTicket(ticket);
    setAssignSupervisor('');
    setAssignWorker('');
    const qtyToAssign = ticket ? (
      ticket.ticket_type === 'QUALITY' 
        ? parseInt(ticket.actual_qty || 0)
        : (parseInt(ticket.shortage_qty) || (parseInt(ticket.expected_qty || 0) - parseInt(ticket.actual_qty || 0)))
    ) : 0;
    setAssignQty(String(qtyToAssign));
    setAssignNotes('');
    setStandardTime('');
    setDueTime('');
    setWorkerSearch('');
    setWorkersList([]);
    setSupervisorsList([]);
    setShowModal(true);
    try {
      const supRes = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/supervisors`);
      if (supRes.data.success) setSupervisorsList(supRes.data.supervisors);
    } catch (err) {
      console.error('Fetch error:', err);
      setSupervisorsList([]);
    }
  };

  const availableQty = selectedTicket ? (
    selectedTicket.ticket_type === 'QUALITY' 
      ? parseInt(selectedTicket.actual_qty || 0)
      : (parseInt(selectedTicket.shortage_qty) || (parseInt(selectedTicket.expected_qty || 0) - parseInt(selectedTicket.actual_qty || 0)))
  ) : 0;

  const filteredMpHistory = mpHistory.filter(item => item.action_type !== 'TRAIN').filter(
    item => (item.worker_name && item.worker_name.toLowerCase().includes(mpHistoryFilterText.toLowerCase())) ||
            (item.worker_id && String(item.worker_id).toLowerCase().includes(mpHistoryFilterText.toLowerCase())) ||
            (item.red_process && item.red_process.toLowerCase().includes(mpHistoryFilterText.toLowerCase())) ||
            (item.source_process && item.source_process.toLowerCase().includes(mpHistoryFilterText.toLowerCase())) ||
            (item.action_type && item.action_type.toLowerCase().includes(mpHistoryFilterText.toLowerCase()))
  );

  const filteredTrainingHistory = trainingHistory.filter(item => {
    const search = trainingHistoryFilterText.toLowerCase();
    return (item.process_name && item.process_name.toLowerCase().includes(search)) ||
      (item.trainer_name && item.trainer_name.toLowerCase().includes(search)) ||
      (item.trainer_id && String(item.trainer_id).toLowerCase().includes(search)) ||
      (item.trainee_name && item.trainee_name.toLowerCase().includes(search)) ||
      (item.trainee_id && String(item.trainee_id).toLowerCase().includes(search)) ||
      (item.skill_name && item.skill_name.toLowerCase().includes(search)) ||
      (item.status && item.status.toLowerCase().includes(search));
  });

  const handleStandardTimeChange = (e) => {
    const mins = e.target.value;
    setStandardTime(mins);
    if (mins && !isNaN(mins) && parseInt(mins) > 0) {
      const now = new Date();
      now.setMinutes(now.getMinutes() + parseInt(mins));
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const localTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      setDueTime(localTime);
    } else {
      setDueTime('');
    }
  };

  const handleDueTimeChange = (e) => {
    const selectedTime = e.target.value;
    setDueTime(selectedTime);
    if (selectedTime) {
      const now = new Date();
      const due = new Date(selectedTime);
      const diffMins = Math.floor((due - now) / 60000);
      if (diffMins > 0) {
        setStandardTime(String(diffMins));
      } else {
        setStandardTime('0');
      }
    } else {
      setStandardTime('');
    }
  };

  const handleAssignRemediation = async () => {
    if (!assignSupervisor || !assignWorker || !assignQty) {
      Swal.fire('Validation', 'Supervisor, Worker, aur quantity teeno required hain.', 'warning');
      return;
    }
    if (parseInt(assignQty) > availableQty) {
      Swal.fire('Quantity Error', `Sirf ${availableQty} pcs ki ${selectedTicket?.ticket_type === 'QUALITY' ? 'rework' : 'shortage'} hai. Isse zyada assign nahi kar sakte!`, 'error');
      return;
    }
    if (parseInt(assignQty) <= 0) {
      Swal.fire('Validation', 'Quantity 0 se zyada honi chahiye.', 'warning');
      return;
    }

    const workerObj = workersList.find(w => String(w.worker_id) === assignWorker);
    if (!workerObj) return;

    try {
      setAssigning(true);
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/manager/assign-remediation`, {
        ticket_id: selectedTicket.ticket_id,
        new_worker_id: assignWorker,
        new_worker_name: workerObj.worker_name,
        qty: parseInt(assignQty),
        assigned_by: user?.Name || user?.EmployeeID || 'MANAGER',
        notes: assignNotes,
        standard_time: standardTime,
        due_time: dueTime,
        verifier: verifier,
        responsible_supervisor: assignSupervisor
      });

      if (res.data.success) {
        setShowModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Remediation Assigned!',
          html: `<b>${workerObj.worker_name}</b> ko <b>${assignQty} pcs</b> ka kaam assign ho gaya.<br/>Ticket <b>#${selectedTicket.ticket_id}</b> update ho gaya.`,
          confirmButtonColor: '#0f172a'
        });
        fetchData();
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Assignment failed', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleVerifyRecovery = async (ticket) => {
    const result = await Swal.fire({
      title: 'Verify recovery?',
      text: `Confirm that lot ${ticket.lot_id} has been physically recovered and accepted.`,
      input: 'textarea',
      inputPlaceholder: 'Verification remark (optional)',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Verify & Close',
      confirmButtonColor: '#15803d'
    });
    if (!result.isConfirmed) return;

    try {
      const response = await axios.post(`${BASE_URL}/api/wip-dashboard/manager/remediation/${ticket.ticket_id}/verify`, {
        verified_by: user?.Name || user?.EmployeeID,
        verification_note: result.value || ''
      });
      if (response.data.success) {
        await Swal.fire('Verified', response.data.message, 'success');
        fetchData();
      }
    } catch (error) {
      Swal.fire('Cannot verify', error.response?.data?.message || 'Verification failed', 'error');
    }
  };

  const handleSlaPause = async (ticket) => {
    const result = await Swal.fire({ title: 'Pause SLA?', input: 'textarea', inputLabel: 'Authorised pause reason', inputPlaceholder: 'Machine breakdown, material issue, safety stop...', showCancelButton: true, inputValidator: value => !value ? 'Reason is required' : undefined });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${BASE_URL}/api/wip-dashboard/tickets/${ticket.ticket_id}/sla-pause`, { reason: result.value, approved_by: user?.Name || user?.EmployeeID });
      Swal.fire('Paused', 'Ticket SLA paused.', 'success'); fetchData();
    } catch (err) { Swal.fire('Error', err.response?.data?.message || 'Could not pause SLA', 'error'); }
  };

  const handleSlaResume = async (ticket) => {
    try {
      await axios.post(`${BASE_URL}/api/wip-dashboard/tickets/${ticket.ticket_id}/sla-resume`, { resumed_by: user?.Name || user?.EmployeeID });
      Swal.fire('Resumed', 'SLA resumed and due time extended.', 'success'); fetchData();
    } catch (err) { Swal.fire('Error', err.response?.data?.message || 'Could not resume SLA', 'error'); }
  };

  const handleManpowerAction = (actionType) => {
    setMpModalType(actionType);
    setMpRedProcess('');
    setMpSourceProcess('');
    setMpAvailableWorkers([]);
    setMpSelectedWorker('');
    setTrainingSkill('');
    setTrainingNotes('');
    setTrainingStartAt('');
    setTrainingEndAt('');
    setTrainingTestResult('');
    setTrainingApprover('');
    setOtStartAt('');
    setOtEndAt('');
    setOtHours('');
    setLendingSupervisor('');
    setExpectedCapacity('');
    setManpowerNotes('');
  };

  const loadWorkersForSource = async (dept) => {
    setMpSourceProcess(dept);
    setMpSelectedWorker('');
    if (!dept) { setMpAvailableWorkers([]); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/manager/workers-list`, {
        params: { dept: dept }
      });
      if (res.data.success) {
        setMpAvailableWorkers(res.data.workers);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRedProcessChange = (selectedOption) => {
    const pId = selectedOption ? selectedOption.value : '';
    setMpRedProcess(pId);
    setMpSelectedWorker('');
    if (mpModalType !== 'BORROW') {
      const p = activeProcesses.find(x => String(x.process_id) === String(pId));
      if (p) {
        loadWorkersForSource(p.name);
      } else {
        setMpAvailableWorkers([]);
      }
    }
  };

  const handleSourceProcessChange = (selectedOption) => {
    const dept = selectedOption ? selectedOption.value : '';
    loadWorkersForSource(dept);
  };

  const submitManpowerAction = async () => {
    if (!mpRedProcess) { Swal.fire('Validation', 'Please select the Red Process', 'warning'); return; }
    if (!mpSelectedWorker) { Swal.fire('Validation', 'Please select a worker', 'warning'); return; }
    if (mpModalType === 'OVERTIME' && (!otStartAt || !otEndAt || !otHours)) { Swal.fire('Validation', 'Please enter OT start, end and total hours.', 'warning'); return; }
    if (mpModalType === 'BORROW' && !lendingSupervisor.trim()) { Swal.fire('Validation', 'Lending supervisor approval is required.', 'warning'); return; }
    if (mpModalType === 'TRAIN' && (!trainingSkill || !trainingStartAt || !trainingEndAt || !trainingTestResult || !trainingApprover.trim())) { Swal.fire('Validation', 'Training schedule, test result and independent approver are required.', 'warning'); return; }
    
    try {
      setSubmittingManpower(true);
      const sourceP = mpModalType === 'BORROW' ? activeProcesses.find(p => p.name === mpSourceProcess) : null;
      
      const payload = {
        process_id: mpRedProcess,
        action_type: mpModalType,
        worker_id: mpSelectedWorker,
        source_process_id: sourceP ? sourceP.process_id : null,
        approved_by: user?.Name || 'Manager',
        ot_start_at: otStartAt || null,
        ot_end_at: otEndAt || null,
        ot_hours: otHours || null,
        lending_supervisor: lendingSupervisor || null,
        expected_capacity: expectedCapacity || null,
        notes: manpowerNotes || null
      };

      if (mpModalType === 'TRAIN') {
        await axios.post(`${BASE_URL}/api/wip-dashboard/manager/training/assign`, { process_id: mpRedProcess, trainer_id: user?.EmployeeID || user?.Name, trainee_id: mpSelectedWorker, skill_name: trainingSkill, start_at: trainingStartAt, end_at: trainingEndAt, planned_approver: trainingApprover, notes: trainingNotes }, { withCredentials: true });
      }
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/manager/manpower/adjust`, payload, { withCredentials: true });

      if (res.data.success) {
        Swal.fire('Success!', res.data.message, 'success');
        setMpModalType(null);
        setMpRedProcess('');
        setMpSourceProcess('');
        setMpSelectedWorker('');
        setMpAvailableWorkers([]);
        fetchManpowerHistory();
        fetchTrainingHistory();
      } else {
        Swal.fire('Error', res.data.message, 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to submit action', 'error');
    } finally {
      setSubmittingManpower(false);
    }
  };

  const decideBorrow = async (row, status) => {
    const result = await Swal.fire({ title: `${status === 'APPROVED' ? 'Approve' : 'Reject'} borrow request?`, input: 'textarea', inputPlaceholder: 'Decision note', showCancelButton: true, confirmButtonText: status === 'APPROVED' ? 'Approve' : 'Reject' });
    if (!result.isConfirmed) return;
    try { await axios.post(`${BASE_URL}/api/wip-dashboard/manager/manpower/${row.id}/approval`, { status, approved_by: user?.Name || user?.EmployeeID, note: result.value }); await fetchManpowerHistory(); Swal.fire('Saved','Borrow decision saved.','success'); }
    catch(err){ Swal.fire('Cannot save',err.response?.data?.message || 'Approval failed','error'); }
  };

  const updateRecovery = async (row, status) => {
    try { await axios.post(`${BASE_URL}/api/wip-dashboard/manager/manpower/${row.id}/recovery-status`, { status, updated_by: user?.Name || user?.EmployeeID }); await fetchManpowerHistory(); }
    catch(err){ Swal.fire('Cannot update',err.response?.data?.message || 'Status update failed','error'); }
  };

  const updateTraining = async (row, status) => {
    let values = {};
    if (status === 'APPROVED' || status === 'REJECTED') {
      const result = await Swal.fire({ title: `${status === 'APPROVED' ? 'Approve' : 'Reject'} training`, html: '<input id="training-result" class="swal2-input" placeholder="Test result / score"><textarea id="training-note" class="swal2-textarea" placeholder="Remark"></textarea>', showCancelButton:true, preConfirm:()=>({ test_result:document.getElementById('training-result').value, note:document.getElementById('training-note').value }) });
      if(!result.isConfirmed) return; values=result.value;
      if(!values.test_result) return Swal.fire('Required','Test result is required.','warning');
    }
    try { await axios.post(`${BASE_URL}/api/wip-dashboard/manager/training/${row.training_id}/status`, { status, ...values, approved_by:(status==='APPROVED'||status==='REJECTED') ? (user?.Name || user?.EmployeeID) : null }); await fetchTrainingHistory(); }
    catch(err){ Swal.fire('Cannot update',err.response?.data?.message || 'Training update failed','error'); }
  };

  const getTimeElapsed = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 60000);
    if (diff < 60) return `${diff} mins ago`;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m ago`;
  };

  const uniqueWorkers = useMemo(() => {
    const workers = new Map();
    tasks.forEach(t => {
      if (t.worker_id && !workers.has(t.worker_id))
        workers.set(t.worker_id, `${t.First_Name} ${t.Last_Name}`.trim());
    });
    return Array.from(workers.entries()).map(([id, name]) => ({ id, name }));
  }, [tasks]);

  const uniqueSupervisors = useMemo(() => {
    const s = new Set();
    tasks.forEach(t => { if (t.supervisor) s.add(t.supervisor); });
    return Array.from(s);
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = t.lot_id?.toLowerCase().includes(q) || t.process_name?.toLowerCase().includes(q) || t.First_Name?.toLowerCase().includes(q) || t.worker_id?.toLowerCase().includes(q);
      const matchesWorker = workerFilter ? t.worker_id === workerFilter : true;
      const matchesSupervisor = supervisorFilter ? t.supervisor === supervisorFilter : true;
      return matchesSearch && matchesWorker && matchesSupervisor;
    });
  }, [tasks, searchQuery, workerFilter, supervisorFilter]);

  const getEscalationStatus = (ticket) => {
    if (ticket.status === 'READY_FOR_VERIFICATION') return 'VERIFY';
    if (ticket.status === 'REMEDIATION_ASSIGNED') return 'ASSIGNED';
    return 'PENDING';
  };

  const escalationCounts = useMemo(() => ({
    ALL: tickets.length,
    PENDING: tickets.filter(ticket => getEscalationStatus(ticket) === 'PENDING').length,
    ASSIGNED: tickets.filter(ticket => getEscalationStatus(ticket) === 'ASSIGNED').length,
    VERIFY: tickets.filter(ticket => getEscalationStatus(ticket) === 'VERIFY').length
  }), [tickets]);

  const filteredEscalationTickets = useMemo(() => {
    const search = escalationSearch.toLowerCase().trim();
    return tickets.filter(ticket => {
      const statusMatches = escalationStatusFilter === 'ALL' || getEscalationStatus(ticket) === escalationStatusFilter;
      const searchMatches = !search || [ticket.ticket_id, ticket.lot_id, ticket.department, ticket.original_worker_name, ticket.issue_type]
        .some(value => String(value || '').toLowerCase().includes(search));
      return statusMatches && searchMatches;
    });
  }, [tickets, escalationStatusFilter, escalationSearch]);

  const escalationPageSize = escalationView === 'grid' ? 6 : 5;
  const escalationTotalPages = Math.max(1, Math.ceil(filteredEscalationTickets.length / escalationPageSize));
  const paginatedEscalationTickets = filteredEscalationTickets.slice(
    (escalationPage - 1) * escalationPageSize,
    escalationPage * escalationPageSize
  );

  useEffect(() => {
    setEscalationPage(1);
  }, [escalationStatusFilter, escalationSearch, escalationView]);

  useEffect(() => {
    if (escalationPage > escalationTotalPages) setEscalationPage(escalationTotalPages);
  }, [escalationPage, escalationTotalPages]);

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>

          {/* PAGE BANNER */}
          <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", padding: "40px 48px", color: "#fff", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(239,68,68,0.2) 100%)", borderRadius: "50%", filter: "blur(40px)" }}></div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
              <div style={{ background: "rgba(239, 68, 68, 0.2)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                <i className="bi bi-shield-lock-fill text-danger" style={{ fontSize: "24px" }}></i>
              </div>
              Manager Action Console
            </h1>
            <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
              Dekhiye kaam kahan chal raha hai, kis ticket par action chahiye, aur worker shortage ka solution choose kijiye.
            </p>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1400px", padding: "0 40px" }}>

            {/* TABS */}
            <ul className="nav nav-pills mb-4" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" }}>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'live' ? 'active' : ''}`} onClick={() => setActiveTab('live')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'live' ? "#0f172a" : "transparent", color: activeTab === 'live' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-activity me-2"></i> 1. Live Work
                  <span className="badge ms-2" style={{ background: activeTab === 'live' ? "rgba(255,255,255,0.2)" : "#e2e8f0", color: activeTab === 'live' ? "#fff" : "#475569" }}>{filteredTasks.length}</span>
                </button>
              </li>
              <li className="nav-item ms-3">
                <button className={`nav-link ${activeTab === 'escalations' ? 'active' : ''}`} onClick={() => setActiveTab('escalations')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'escalations' ? "#dc2626" : "transparent", color: activeTab === 'escalations' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-exclamation-triangle-fill me-2"></i> 2. Action Required
                  <span className="badge ms-2" style={{ background: activeTab === 'escalations' ? "rgba(255,255,255,0.2)" : "#fee2e2", color: activeTab === 'escalations' ? "#fff" : "#dc2626" }}>{tickets.length}</span>
                </button>
              </li>
              <li className="nav-item ms-3">
                <button className={`nav-link ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'reports' ? "#0f766e" : "transparent", color: activeTab === 'reports' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-bar-chart-fill me-2"></i> 4. Reports
                </button>
              </li>
              <li className="nav-item ms-3">
                <button className={`nav-link ${activeTab === 'manpower' ? 'active' : ''}`} onClick={() => setActiveTab('manpower')}
                  style={{ fontWeight: 700, borderRadius: "10px", padding: "10px 24px", background: activeTab === 'manpower' ? "#d97706" : "transparent", color: activeTab === 'manpower' ? "#fff" : "#64748b" }}>
                  <i className="bi bi-people-fill me-2"></i> 3. Worker Shortage
                  <span className="badge ms-2" style={{ background: activeTab === 'manpower' ? "rgba(255,255,255,0.2)" : "#fef3c7", color: activeTab === 'manpower' ? "#fff" : "#d97706" }}>{mpHistory.length}</span>
                </button>
              </li>
            </ul>

            <div className="alert border-0 mb-4" style={{ background: "#eff6ff", color: "#1e3a8a", borderRadius: "12px", padding: "14px 18px" }}>
              <div style={{ fontWeight: 800, marginBottom: "4px" }}><i className="bi bi-info-circle-fill me-2"></i>Is screen ko kaise use karein?</div>
              <div style={{ fontSize: "14px" }}>
                <b>Live Work</b> mein current work dekhein → <b>Action Required</b> mein pending ticket solve karein → <b>Worker Shortage</b> mein OT, Borrow ya Training choose karein.
              </div>
            </div>

            {loading ? (
              <div className="text-center" style={{ padding: "100px 0" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                <h5 className="mt-4 text-secondary" style={{ fontWeight: 600 }}>Syncing floor data...</h5>
              </div>
            ) : (
              <div>
                {/* TAB 1: LIVE WORKER STATUS */}
                {activeTab === 'live' && (
                  <div>
                    {/* FILTERS */}
                    <div className="card border-0 mb-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", background: "#fff" }}>
                      <div className="card-body p-4 d-flex gap-3 flex-wrap">
                        <div style={{ flex: "1 1 250px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Search Active Floor</label>
                          <div className="input-group">
                            <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                            <input type="text" className="form-control border-start-0 ps-0" placeholder="Lot ID, Process, Worker..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ boxShadow: "none" }} />
                          </div>
                        </div>
                        {user?.Auth === 'SuperAdmin' && (
                          <div style={{ flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Supervisor</label>
                            <select className="form-select" value={supervisorFilter} onChange={e => setSupervisorFilter(e.target.value)} style={{ boxShadow: "none" }}>
                              <option value="">All Supervisors</option>
                              {uniqueSupervisors.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        )}
                        <div style={{ flex: "1 1 200px" }}>
                          <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Worker</label>
                          <select className="form-select" value={workerFilter} onChange={e => setWorkerFilter(e.target.value)} style={{ boxShadow: "none" }}>
                            <option value="">All Workers</option>
                            {uniqueWorkers.map(w => <option key={w.id} value={w.id}>{w.name} ({w.id})</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0" style={{ borderRadius: "20px", overflow: "hidden", background: "#fff", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table align-middle mb-0" style={{ fontSize: "15px" }}>
                            <thead style={{ background: "#1e293b" }}>
                              <tr>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Worker Info</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Lot / Process</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Supervisor</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Target</th>
                                <th style={{ color: "#fff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px" }}>Live Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTasks.length === 0 ? (
                                <tr><td colSpan="5" className="text-center text-muted" style={{ padding: "60px 0" }}>
                                  <i className="bi bi-inbox" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                                  <div className="mt-3" style={{ fontWeight: 600 }}>No active workers match your filters</div>
                                </td></tr>
                              ) : filteredTasks.map(t => (
                                <tr key={t.task_id} style={{ transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#f8fafc"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                                  <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                                    <div style={{ fontWeight: 700, color: "#1e293b" }}>{t.First_Name} {t.Last_Name}</div>
                                    <div style={{ color: "#64748b", fontSize: "13px", marginTop: "4px" }}>ID: {t.worker_id}</div>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>{t.lot_id}</div>
                                    <div className="badge mt-2" style={{ background: "#e2e8f0", color: "#334155", fontWeight: 600, marginRight: "8px" }}>{t.process_name}</div>
                                    {t.notes && (t.notes.includes("Remediation") || t.notes.includes("Std Time:")) && (
                                      <div style={{ marginTop: "12px" }}>
                                        <span className="badge" style={{ background: "#fef3c7", color: "#d97706", border: "1px solid #fde68a", fontSize: "11px", fontWeight: 700 }}>
                                          <i className="bi bi-wrench-adjustable me-1"></i> Remediation Task
                                        </span>
                                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: "6px", lineHeight: "1.4", background: "#f8fafc", padding: "6px", borderRadius: "6px", border: "1px dashed #cbd5e1" }}>
                                          {t.notes.split('|').map((part, i) => (
                                            <div key={i}>{part.trim()}</div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>{t.supervisor || '-'}</span>
                                  </td>
                                  <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0", fontWeight: 800, color: "#3b82f6" }}>
                                    {t.target_qty > 0 ? `${t.target_qty} pcs` : '-'}
                                  </td>
                                  <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                                    {t.status === 'IN_PROGRESS' && (
                                      <div>
                                        <span style={{ display: "flex", alignItems: "center", background: "#ecfdf5", color: "#059669", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px", width: "fit-content" }}>
                                          <span style={{ width: "8px", height: "8px", background: "#10b981", borderRadius: "50%", marginRight: "8px", boxShadow: "0 0 8px #10b981" }}></span> Running
                                        </span>
                                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>{getTimeElapsed(t.start_time)}</div>
                                      </div>
                                    )}
                                    {t.status === 'SLA_BREACHED' && (
                                      <span style={{ display: "flex", flexDirection: "column", alignItems: "center", background: "#fef2f2", color: "#dc2626", padding: "6px 12px", borderRadius: "8px", fontWeight: 700, fontSize: "12px", width: "fit-content", border: "1px solid #fecaca" }}>
                                        <span><i className="bi bi-exclamation-triangle-fill me-1"></i> Timeout</span>
                                        <span style={{ fontSize: "10px", opacity: 0.9 }}>Ticket Raised</span>
                                      </span>
                                    )}
                                    {t.status === 'DONE' && (
                                      <div>
                                        <span style={{ display: "flex", alignItems: "center", background: "#f0f9ff", color: "#0284c7", padding: "6px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "13px", width: "fit-content" }}>
                                          <i className="bi bi-check-all me-2"></i> Completed
                                        </span>
                                        <div style={{ marginTop: "10px", fontSize: "12px", color: "#475569", background: "#f8fafc", padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                          <div><strong>Done:</strong> {t.completed_qty} pcs</div>
                                          {t.rejected_qty > 0 && <div className="text-danger mt-1"><strong>Rejected:</strong> {t.rejected_qty} pcs</div>}
                                          {t.hold_reason && <div className="mt-1"><strong>Note:</strong> {t.hold_reason}</div>}
                                          {t.photo_url && (
                                            <div className="mt-2">
                                              <strong style={{fontSize: '11px', display: 'block', marginBottom: '4px'}}>Completed Photo(s):</strong>
                                              <div className="d-flex overflow-auto" style={{ gap: "8px", paddingBottom: "4px" }}>
                                                {t.photo_url.split(',').map((imgUrl, idx) => (
                                                  <div key={idx} onClick={() => setPreviewImage(getPhotoUrl(imgUrl.trim()))} style={{ cursor: "pointer", flexShrink: 0 }}>
                                                    <img src={getPhotoUrl(imgUrl.trim())} alt={`Completed ${idx+1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #22c55e' }} />
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {t.rejected_photo_url && (
                                            <div className="mt-2">
                                              <strong style={{fontSize: '11px', display: 'block', marginBottom: '4px', color: '#dc2626'}}>Rejected Photo(s):</strong>
                                              <div className="d-flex overflow-auto" style={{ gap: "8px", paddingBottom: "4px" }}>
                                                {t.rejected_photo_url.split(',').map((imgUrl, idx) => (
                                                  <div key={idx} onClick={() => setPreviewImage(getPhotoUrl(imgUrl.trim()))} style={{ cursor: "pointer", flexShrink: 0 }}>
                                                    <img src={getPhotoUrl(imgUrl.trim())} alt={`Rejected ${idx+1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #ef4444' }} />
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: ESCALATIONS */}
                {activeTab === 'escalations' && (
                  <div className="card border-0" style={{ borderRadius: "20px", background: "linear-gradient(145deg, #ffffff, #fef2f2)", boxShadow: "0 10px 25px -5px rgba(239,68,68,0.1)" }}>
                    <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-start gap-3 flex-wrap" style={{ padding: "30px 30px 20px" }}>
                      <div>
                        <h5 style={{ fontWeight: 800, color: "#991b1b", margin: 0, fontSize: "20px" }}>
                          <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i> Tickets needing your decision
                        </h5>
                        <p style={{ color: "#7f1d1d", margin: "6px 0 0", fontSize: "14px", fontWeight: 500, opacity: 0.8 }}>
                          Har card mein pehle problem dekhein, phir neeche <b>Assign worker</b> se work complete karwayein.
                        </p>
                      </div>
                      <div className="btn-group" role="group" aria-label="Ticket display view">
                        <button type="button" title="Grid view" onClick={() => setEscalationView('grid')} className={`btn btn-sm ${escalationView === 'grid' ? 'btn-dark' : 'btn-outline-secondary'}`} style={{ fontWeight: 700, padding: '8px 14px' }}><i className="bi bi-grid-3x3-gap-fill me-1"></i>Grid</button>
                        <button type="button" title="List view" onClick={() => setEscalationView('list')} className={`btn btn-sm ${escalationView === 'list' ? 'btn-dark' : 'btn-outline-secondary'}`} style={{ fontWeight: 700, padding: '8px 14px' }}><i className="bi bi-list-ul me-1"></i>List</button>
                      </div>
                    </div>
                    <div className="card-body p-0 px-4 pb-4">
                      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px' }}>
                        <div>
                          <div className="text-muted mb-2" style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Filter by ticket stage</div>
                        <div className="d-flex gap-2 flex-wrap" role="group" aria-label="Ticket status filter">
                          {[
                            ['ALL', 'All tickets', 'bi-collection', '#334155', '#f1f5f9'],
                            ['PENDING', 'Pending action', 'bi-exclamation-circle', '#b45309', '#fffbeb'],
                            ['ASSIGNED', 'Worker assigned', 'bi-person-check', '#2563eb', '#eff6ff'],
                            ['VERIFY', 'Check & close', 'bi-check2-circle', '#15803d', '#f0fdf4']
                          ].map(([value, label, icon, color, background]) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setEscalationStatusFilter(value)}
                              style={{
                                border: escalationStatusFilter === value ? `2px solid ${color}` : '1px solid #e2e8f0',
                                background: escalationStatusFilter === value ? background : '#fff',
                                color,
                                borderRadius: '10px',
                                padding: '8px 12px',
                                fontWeight: 800,
                                fontSize: '13px',
                                transition: 'all 0.2s'
                              }}
                            >
                              <i className={`bi ${icon} me-1`}></i>{label} <span className="badge ms-1" style={{ background: color, color: '#fff', borderRadius: '20px' }}>{escalationCounts[value]}</span>
                            </button>
                          ))}
                        </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="input-group" style={{ width: '290px' }}>
                          <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search ticket, lot, department..."
                            value={escalationSearch}
                            onChange={event => setEscalationSearch(event.target.value)}
                          />
                        </div>
                      </div>
                      </div>
                      {tickets.length === 0 ? (
                        <div className="text-center" style={{ padding: "80px 0" }}>
                          <div style={{ width: "80px", height: "80px", background: "#dcfce7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                            <i className="bi bi-check2 text-success" style={{ fontSize: "40px" }}></i>
                          </div>
                          <h5 className="mt-4" style={{ color: "#166534", fontWeight: 800 }}>Koi pending action nahi hai</h5>
                          <p style={{ color: "#15803d", fontWeight: 500 }}>Abhi manager action ke liye koi ticket pending nahi hai.</p>
                        </div>
                      ) : (
                        <div className="row g-4">
                          {paginatedEscalationTickets.map(t => (
                            <div key={t.ticket_id} className={escalationView === 'grid' ? 'col-xl-4 col-lg-6 col-md-12' : 'col-12'}>
                              <div className="card border-0 h-100" style={{ borderRadius: "16px", background: "#fff", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #fca5a5" }}>
                                <div className="card-body p-4">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="badge" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", padding: "6px 12px", borderRadius: "8px", fontWeight: 800 }}>
                                      TICKET #{t.ticket_id}
                                    </span>
                                    <span style={{ fontSize: "11px", fontWeight: 800, color: "#ef4444", textTransform: "uppercase", letterSpacing: "1px", background: "#fef2f2", padding: "4px 10px", borderRadius: "20px" }}>
                                      {t.status === 'READY_FOR_VERIFICATION' ? 'CHECK & CLOSE' : t.status === 'REMEDIATION_ASSIGNED' ? 'WORK ASSIGNED' : 'PENDING ACTION'}
                                    </span>
                                  </div>

                                  <h5 style={{ fontWeight: 800, color: "#0f172a", margin: "0 0 12px 0", fontSize: "18px" }}>
                                    <i className="bi bi-box me-2 text-muted" style={{ fontSize: "14px" }}></i>{t.lot_id}
                                  </h5>

                                  <div style={{ background: "#f8fafc", padding: "12px 16px", borderRadius: "12px", marginBottom: "16px" }}>
                                    <div className="d-flex justify-content-between mb-3">
                                      <span style={{ fontSize: "12px", fontWeight: 700, padding: "4px 10px", borderRadius: "20px", background: t.ticket_type === 'QUALITY' ? "#fef3c7" : t.ticket_type === 'HANDOVER_REJECT' ? "#dbeafe" : "#fee2e2", color: t.ticket_type === 'QUALITY' ? "#b45309" : t.ticket_type === 'HANDOVER_REJECT' ? "#1d4ed8" : "#b91c1c" }}>
                                        <i className={t.ticket_type === 'QUALITY' ? "bi bi-shield-exclamation me-1" : t.ticket_type === 'HANDOVER_REJECT' ? "bi bi-arrow-left-right me-1" : t.ticket_type === 'RECOVERY' ? "bi bi-wrench-adjustable me-1" : "bi bi-clock-history me-1"}></i>
                                        {t.ticket_type === 'QUALITY' ? 'QC NE QUALITY ISSUE BHEJA HAI' : t.ticket_type === 'HANDOVER_REJECT' ? 'NEXT DEPARTMENT NE LOT REJECT KIYA' : t.ticket_type === 'RECOVERY' ? 'ASSIGNED WORK KA TIME NIKAL GAYA' : 'WORK TIME PAR COMPLETE NAHI HUA'}
                                      </span>
                                    </div>
                                    <div className="mb-3" style={{ background: "#fff", padding: "10px 12px", borderRadius: "8px", borderLeft: "3px solid #ef4444" }}>
                                      <div style={{ color: "#991b1b", fontWeight: 800, fontSize: "12px", marginBottom: "3px" }}>PROBLEM KYA HAI?</div>
                                      <div style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.issue_type || (t.ticket_type === 'QUALITY' ? 'Quality check mein issue mila hai.' : 'Is work par manager action chahiye.')}</div>
                                    </div>
                                    <div className="d-flex mb-2">
                                      <span style={{ width: "90px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Department:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.department || '-'}</span>
                                    </div>
                                    <div className="d-flex mb-2">
                                      <span style={{ width: "90px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Current worker:</span>
                                      <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>{t.original_worker_name || '-'}</span>
                                    </div>
                                    {(t.ticket_type === 'QUALITY' || t.ticket_type === 'HANDOVER_REJECT') && (
                                      <>
                                        <div className="d-flex mb-2">
                                          <span style={{ width: "90px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Qty details:</span>
                                          <span style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>
                                            Expected: {t.expected_qty} | <span className="text-danger">Checked: {t.actual_qty}</span>
                                          </span>
                                        </div>
                                        {t.comments && (
                                          <div className="mb-2">
                                            <span style={{ display: "block", color: "#64748b", fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>Comments:</span>
                                            <div style={{ background: "#fff", padding: "8px", borderRadius: "6px", fontSize: "13px", color: "#334155", border: "1px dashed #cbd5e1" }}>
                                              {t.comments}
                                            </div>
                                          </div>
                                        )}
                                        {t.photo_url && (
                                          <div className="mt-2">
                                            <span style={{ display: "block", color: "#64748b", fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>Photos:</span>
                                            <div className="d-flex overflow-auto pb-2" style={{ gap: "10px" }}>
                                              {t.photo_url.split(',').map((url, idx) => (
                                                <div key={idx} onClick={() => setPreviewImage(getPhotoUrl(url.trim()))} style={{ cursor: "pointer", flexShrink: 0 }}>
                                                  <img src={getPhotoUrl(url.trim())} alt={`Defect ${idx+1}`} style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  <div className="d-flex mt-2 pt-2 border-top">
                                      <span style={{ width: "90px", color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Reported at:</span>
                                      <span style={{ color: "#475569", fontWeight: 600, fontSize: "13px" }}>{t.created_at ? new Date(t.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2 mb-2">
                                    {t.sla_paused_at ? <button className="btn btn-outline-success btn-sm flex-fill" onClick={() => handleSlaResume(t)}>Resume time tracking</button> : <button className="btn btn-outline-warning btn-sm flex-fill" onClick={() => handleSlaPause(t)}>Pause time tracking</button>}
                                  </div>

                                  {t.status === 'READY_FOR_VERIFICATION' ? (
                                    <button
                                      className="btn w-100"
                                      style={{ background: "linear-gradient(135deg, #15803d, #166534)", color: "#fff", fontWeight: 700, borderRadius: "10px", padding: "12px", fontSize: "14px" }}
                                      onClick={() => handleVerifyRecovery(t)}
                                    >
                                      <i className="bi bi-check2-circle me-2"></i> CHECK WORK & CLOSE TICKET
                                    </button>
                                  ) : t.status === 'REMEDIATION_ASSIGNED' ? (
                                    <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "10px", padding: "12px 16px", textAlign: "center" }}>
                                      <i className="bi bi-person-check-fill text-success me-2"></i>
                                        <span style={{ color: "#166534", fontWeight: 700, fontSize: "14px" }}>Worker assigned — completion ka wait hai</span>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn w-100"
                                      style={{ background: "linear-gradient(135deg, #dc2626, #991b1b)", color: "#fff", fontWeight: 700, borderRadius: "10px", padding: "12px", fontSize: "14px", transition: "all 0.2s", boxShadow: "0 4px 12px rgba(220,38,38,0.3)" }}
                                      onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(220,38,38,0.4)"; }}
                                      onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(220,38,38,0.3)"; }}
                                      onClick={() => openRemediationModal(t)}
                                    >
                                      <i className="bi bi-person-plus-fill me-2"></i> ASSIGN WORKER TO FIX THIS
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {filteredEscalationTickets.length === 0 && (
                            <div className="col-12">
                              <div className="text-center text-muted py-5 bg-white rounded-4 border">Is filter ke hisaab se koi ticket nahi mila.</div>
                            </div>
                          )}
                        </div>
                      )}
                      {filteredEscalationTickets.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-4 pt-3 border-top">
                          <span className="text-muted" style={{ fontSize: '14px', fontWeight: 600 }}>
                            Showing {Math.min((escalationPage - 1) * escalationPageSize + 1, filteredEscalationTickets.length)}–{Math.min(escalationPage * escalationPageSize, filteredEscalationTickets.length)} of {filteredEscalationTickets.length} tickets
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={escalationPage === 1} onClick={() => setEscalationPage(page => page - 1)}><i className="bi bi-chevron-left"></i> Previous</button>
                            <span className="badge bg-dark px-3 py-2">Page {escalationPage} of {escalationTotalPages}</span>
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={escalationPage === escalationTotalPages} onClick={() => setEscalationPage(page => page + 1)}>Next <i className="bi bi-chevron-right"></i></button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* TAB 3: RED PROCESS / MANPOWER */}
                {activeTab === 'manpower' && (
                  <div className="card border-0" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
                    <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
                      <h4 style={{ fontWeight: 800, color: "#0f172a", margin: 0 }}>Worker kam hain — kya karein?</h4>
                      <p className="text-muted mt-2 mb-2">Neeche diye order mein option choose karein. Pehla option enough na ho, tab agla option use karein.</p>
                      <div style={{ background: "#fff7ed", color: "#9a3412", borderRadius: "10px", padding: "10px 14px", fontSize: "14px", fontWeight: 700 }}>
                        Recommended order: 1. Overtime → 2. Borrow Worker → 3. Train Worker
                      </div>
                    </div>
                    <div className="card-body p-4">
                      <div className="row g-4">
                        <div className="col-md-4">
                          <div className="p-4" style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <h5 style={{ fontWeight: 700, color: "#d97706" }}><i className="bi bi-clock-history me-2"></i> Step 1: Overtime</h5>
                            <p style={{ fontSize: "14px", color: "#64748b" }}>Isi department ke available worker ko extra time dein.</p>
                            <button onClick={() => handleManpowerAction('OVERTIME')} className="btn w-100" style={{ background: "#d97706", color: "#fff", fontWeight: 600 }}>Use Overtime</button>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-4" style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <h5 style={{ fontWeight: 700, color: "#2563eb" }}><i className="bi bi-arrow-left-right me-2"></i> Step 2: Borrow Worker</h5>
                            <p style={{ fontSize: "14px", color: "#64748b" }}>Dusre department se trained worker temporary lein.</p>
                            <button onClick={() => handleManpowerAction('BORROW')} className="btn w-100" style={{ background: "#2563eb", color: "#fff", fontWeight: 600 }}>Borrow Worker</button>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-4" style={{ background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                            <h5 style={{ fontWeight: 700, color: "#dc2626" }}><i className="bi bi-mortarboard-fill me-2"></i> Step 3: Train Worker</h5>
                            <p style={{ fontSize: "14px", color: "#64748b" }}>Worker ko required skill ki training assign karein.</p>
                            <button onClick={() => handleManpowerAction('TRAIN')} className="btn w-100" style={{ background: "#dc2626", color: "#fff", fontWeight: 600 }}>Start Training</button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Manpower Adjustment History Table - Moved to Main View */}
                    <div className="px-4 pb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 style={{ fontWeight: 700, color: "#1e293b", margin: 0 }}>Overtime / Borrow ka record</h5>
                          <div className="text-muted mt-1" style={{ fontSize: "14px" }}>Kis department ke liye kis worker ko overtime diya ya doosre department se borrow kiya—woh yahan dikhega.</div>
                        </div>
                        <input
                          type="text"
                          placeholder="Search Overtime / Borrow..."
                          className="form-control"
                          style={{ width: '250px', borderRadius: '10px' }}
                          value={mpHistoryFilterText}
                          onChange={(e) => setMpHistoryFilterText(e.target.value)}
                        />
                      </div>
                      <div className="table-responsive" style={{ border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
                        <table className="table table-sm align-middle mb-0">
                          <thead><tr><th>Time</th><th>Action</th><th>Worker</th><th>Department needing help</th><th>Approval</th><th>Capacity recovery</th><th>Next action</th></tr></thead>
                          <tbody>
                            {filteredMpHistory.length ? filteredMpHistory.map(row => (
                              <tr key={row.id || `${row.start_time}-${row.worker_id}`}>
                                <td>{row.start_time ? new Date(row.start_time).toLocaleString() : '-'}</td>
                                <td><span className={`badge ${row.action_type === 'BORROW' ? 'bg-primary' : 'bg-warning text-dark'}`}>{row.action_type}</span></td>
                                <td>{row.worker_name || '-'} {row.worker_id ? `(${row.worker_id})` : ''}</td>
                                <td>{row.red_process || '-'}</td>
                                <td>{row.action_type === 'OVERTIME' ? `${row.ot_hours || '-'} hrs` : <>{row.source_process || '-'}<br /><small>{row.lending_supervisor || '-'}</small></>}<br/><span className={`badge ${row.approval_status==='APPROVED'?'bg-success':row.approval_status==='REJECTED'?'bg-danger':'bg-warning text-dark'}`}>{row.approval_status || 'PENDING'}</span></td>
                                <td>{row.expected_capacity ? `${row.expected_capacity} pcs` : '-'}<br/><span className={`badge ${row.recovery_status==='GREEN'?'bg-success':row.recovery_status==='AMBER'?'bg-warning text-dark':'bg-danger'}`}>{row.recovery_status || 'RED'}</span></td>
                                <td><div className="d-flex flex-wrap gap-1">{row.action_type==='BORROW' && row.approval_status==='PENDING' && <><button className="btn btn-sm btn-success" onClick={()=>decideBorrow(row,'APPROVED')}>Approve</button><button className="btn btn-sm btn-outline-danger" onClick={()=>decideBorrow(row,'REJECTED')}>Reject</button></>}{row.approval_status==='APPROVED' && row.recovery_status!=='GREEN' && <><button className="btn btn-sm btn-warning" onClick={()=>updateRecovery(row,'AMBER')}>Amber</button><button className="btn btn-sm btn-success" onClick={()=>updateRecovery(row,'GREEN')}>Capacity restored</button></>}</div></td>
                              </tr>
                            )) : <tr><td colSpan="7" className="text-center text-muted py-3">{mpHistory.length ? 'Search ke hisaab se koi Overtime / Borrow record nahi mila.' : 'Abhi tak Overtime ya Borrow Worker use nahi hua.'}</td></tr>}
                          </tbody>
                        </table>
                      </div>
                      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-4 mb-3">
                        <div>
                          <h5 style={{ fontWeight: 700, margin: 0 }}>Worker Training ka record</h5>
                          <div className="text-muted mt-1" style={{ fontSize: "14px" }}>“Start Training” se kis worker ko, kis skill ki training assign hui aur uska current status kya hai—woh yahan dikhega.</div>
                        </div>
                        <input
                          type="text"
                          placeholder="Search Training..."
                          className="form-control"
                          style={{ width: '250px', borderRadius: '10px' }}
                          value={trainingHistoryFilterText}
                          onChange={(e) => setTrainingHistoryFilterText(e.target.value)}
                        />
                      </div>
                      <div className="table-responsive"><table className="table table-sm align-middle"><thead><tr><th>Department</th><th>Trainer</th><th>Trainee</th><th>Skill / test</th><th>Start / end</th><th>Independent approval</th><th>Status / action</th></tr></thead><tbody>{filteredTrainingHistory.length ? filteredTrainingHistory.map(row => <tr key={row.training_id}><td>{row.process_name || row.process_id || '-'}</td><td>{row.trainer_name || row.trainer_id}</td><td>{row.trainee_name || row.trainee_id}</td><td>{row.skill_name}<br /><small>{row.test_result || 'Test pending'}</small></td><td>{row.start_at ? new Date(row.start_at).toLocaleString() : '-'}<br /><small>to {row.end_at ? new Date(row.end_at).toLocaleString() : 'Pending'}</small></td><td>{row.approved_by || row.planned_approver || 'Pending'}</td><td><span className={`badge ${row.status==='APPROVED'?'bg-success':row.status==='REJECTED'?'bg-danger':'bg-secondary'}`}>{row.status}</span><div className="mt-1 d-flex gap-1">{row.status==='ASSIGNED'&&<button className="btn btn-sm btn-primary" onClick={()=>updateTraining(row,'IN_PROGRESS')}>Start</button>}{row.status==='IN_PROGRESS'&&<button className="btn btn-sm btn-warning" onClick={()=>updateTraining(row,'COMPLETED')}>Complete</button>}{row.status==='COMPLETED'&&<><button className="btn btn-sm btn-success" onClick={()=>updateTraining(row,'APPROVED')}>Approve</button><button className="btn btn-sm btn-outline-danger" onClick={()=>updateTraining(row,'REJECTED')}>Reject</button></>}</div></td></tr>) : <tr><td colSpan="7" className="text-center text-muted py-3">{trainingHistory.length ? 'Search ke hisaab se koi training record nahi mila.' : 'Abhi tak kisi worker ko training assign nahi hui.'}</td></tr>}</tbody></table></div>
                    </div>
                  </div>
                )}
                {activeTab === 'reports' && (
                  <div className="card border-0" style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div className="card-header bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                      <div><h4 className="mb-1" style={{ fontWeight: 800, color: '#172554' }}>Management reports</h4><div className="text-muted">Yahan se dekhein: kaunsa issue repeat ho raha hai aur recovery work kahan pending hai.</div></div>
                      <button className="btn btn-outline-secondary btn-sm" onClick={fetchWorkflowReport}><i className="bi bi-arrow-clockwise me-1"></i>Refresh</button>
                    </div>
                    <div className="card-body p-4">
                      <div className="alert border-0 mb-4" style={{ background: '#eff6ff', color: '#1e3a8a', borderRadius: '12px' }}><b>Use kaise karein:</b> Pehle <b>Pending action</b> dekhein, phir top defect ko fix karein, aur recovery worker assigned tickets ko close karwayein.</div>
                      <div className="row g-3 mb-4">
                        {[
                          ['Pending tickets', workflowReport.ageing.filter(row => ['OPEN', 'TEMPORARY', 'ACKNOWLEDGED'].includes(row.status)).reduce((sum, row) => sum + Number(row.ticket_count || 0), 0), 'bi-exclamation-circle', '#d97706'],
                          ['Recovery pending', workflowReport.recovery.filter(row => row.status !== 'REMEDIATION_VERIFIED').reduce((sum, row) => sum + Number(row.recovery_count || 0), 0), 'bi-arrow-repeat', '#2563eb'],
                          ['Repeat failures', workflowReport.repeat_failures.length, 'bi-person-exclamation', '#dc2626'],
                          ['False-ticket reviews', workflowReport.false_tickets.length, 'bi-shield-exclamation', '#7c3aed']
                        ].map(([label, value, icon, color]) => <div className="col-sm-6 col-xl-3" key={label}><div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}><div style={{ width: 40, height: 40, borderRadius: '12px', display: 'grid', placeItems: 'center', background: `${color}18`, color }}><i className={`bi ${icon}`}></i></div><div><div className="text-muted" style={{ fontSize: '12px', fontWeight: 700 }}>{label}</div><div style={{ fontSize: '24px', fontWeight: 800, color: '#172554' }}>{value}</div></div></div></div>)}
                      </div>
                      <div className="row g-4">
                      <div className="col-lg-6"><ReportTable icon="bi-clock-history" color="#d97706" title="Pending ticket time" description="Kaunsa ticket kitne time se open hai." headers={['Current stage', 'Tickets', 'Average wait']} rows={workflowReport.ageing.map(row => [readableTicketStatus(row.status), row.ticket_count, `${Math.round((row.avg_age_minutes || 0) / 60 * 10) / 10} hrs`])} /></div>
                      <div className="col-lg-6"><ReportTable icon="bi-bug" color="#dc2626" title="Top quality problems" description="Sabse zyada repeat hone wale defect types." headers={['Problem', 'Times reported']} rows={workflowReport.defects.map(row => [row.category, row.issue_count])} /></div>
                      <div className="col-lg-6"><ReportTable icon="bi-person-exclamation" color="#7c3aed" title="Repeat failures" description="Jinke permanent incidents repeat ho rahe hain." headers={['Person', 'Permanent incidents']} rows={workflowReport.repeat_failures.map(row => [row.responsible_person, row.incident_count])} /></div>
                      <div className="col-lg-6"><ReportTable icon="bi-arrow-repeat" color="#2563eb" title="Recovery work" description="New worker se recovery task ka current stage." headers={['Recovery stage', 'Count']} rows={workflowReport.recovery.map(row => [readableTicketStatus(row.status), row.recovery_count])} /></div>
                      <div className="col-12"><ReportTable icon="bi-shield-exclamation" color="#7c3aed" title="False ticket review" description="Sirf proven false/malicious ticket yahan aayenge; normal ticket nahi." headers={['Ticket', 'Lot', 'Raised by', 'Issue', 'Review status', 'Reported']} rows={workflowReport.false_tickets.map(row => [`#${row.ticket_id}`, row.lot_id, row.raised_by, row.defect_category || '-', readableTicketStatus(row.status), row.created_at ? new Date(row.created_at).toLocaleString() : '-'])} /></div>
                    </div></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── REMEDIATION ASSIGN MODAL ── */}
      {showModal && selectedTicket && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(6px)" }}>
          <div style={{ background: "#fff", borderRadius: "24px", width: "100%", maxWidth: "700px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 60px rgba(0,0,0,0.3)", animation: "slideUpModal 0.25s ease" }}>
            
            {/* Modal Header */}
            <div style={{ background: "#fff", padding: "24px 28px", borderRadius: "24px 24px 0 0", borderBottom: "2px solid #f1f5f9", flexShrink: 0 }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="bi bi-exclamation-triangle-fill"></i> Ticket #{selectedTicket.ticket_id}
                  </div>
                  <h4 style={{ margin: 0, fontWeight: 800, fontSize: "20px", color: "#0f172a" }}>Assign Remediation Task</h4>
                  <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 500 }}>
                    Lot: <b style={{ color: "#0f172a" }}>{selectedTicket.lot_id}</b> · Dept: <b style={{ color: "#0f172a" }}>{selectedTicket.department || 'N/A'}</b>
                  </p>
                </div>
                <button onClick={() => setShowModal(false)} style={{ background: "#f1f5f9", border: "none", color: "#64748b", borderRadius: "10px", width: "36px", height: "36px", cursor: "pointer", fontSize: "18px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "28px 32px", overflowY: "auto", flexGrow: 1 }}>
              
              {/* Info Banner */}
              <div style={{ background: "#fef9c3", border: "1px solid #fde047", borderRadius: "12px", padding: "12px 16px", marginBottom: "24px", display: "flex", gap: "10px" }}>
                <i className="bi bi-info-circle-fill text-warning mt-1"></i>
                <div style={{ fontSize: "13px", color: "#713f12", fontWeight: 600 }}>
                  Original worker <b style={{ textTransform: "uppercase", color: "#dc2626" }}>({selectedTicket?.original_worker_name || 'Unknown'})</b> <b>cannot</b> be assigned. Please choose a <b>different worker</b> for this remediation task.
                </div>
              </div>

              {/* Supervisor Search + Select */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-shield-check me-2 text-indigo"></i> Responsible Supervisor *
                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", marginLeft: "8px", textTransform: "none" }}>
                    (For the generated Recovery Ticket)
                  </span>
                </label>
                <Select
                  options={supervisorsList.map(s => ({ value: s.supervisor_name, label: s.supervisor_name }))}
                  value={assignSupervisor ? { value: assignSupervisor, label: assignSupervisor } : null}
                  onChange={(selectedOption) => setAssignSupervisor(selectedOption ? selectedOption.value : '')}
                  isClearable
                  placeholder="Select Responsible Supervisor..."
                  isLoading={supervisorsList.length === 0}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "12px",
                      padding: "4px 8px",
                      borderColor: state.isFocused ? "#4f46e5" : "#e2e8f0",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(79, 70, 229, 0.1)" : "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: "#cbd5e1" }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: "12px",
                      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                      overflow: "hidden"
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontWeight: 600,
                      backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused ? "#e0e7ff" : "white",
                      color: state.isSelected ? "white" : "#1e293b",
                      cursor: "pointer",
                      padding: "10px 16px",
                      "&:hover": { backgroundColor: state.isSelected ? "#1d4ed8" : "#f1f5f9" }
                    })
                  }}
                />
              </div>

              {/* Worker Search + Select */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-person-fill me-2 text-primary"></i> Select Worker *
                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", marginLeft: "8px", textTransform: "none" }}>
                    ({workersList.length > 0 ? workersList[0].Department : user?.Department} dept — {workersList.filter(w => w.worker_name !== selectedTicket?.original_worker_name).length} eligible workers)
                  </span>
                </label>
                <Select
                  options={workersList.filter(w => w.worker_name !== selectedTicket?.original_worker_name).map(w => ({ value: w.worker_id, label: w.worker_name }))}
                  value={assignWorker ? { value: assignWorker, label: workersList.find(w => w.worker_id === assignWorker)?.worker_name } : null}
                  onChange={(selectedOption) => setAssignWorker(selectedOption ? selectedOption.value : '')}
                  isClearable
                  placeholder={assignSupervisor ? "Search eligible worker by name..." : "Please select a supervisor first"}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "12px",
                      padding: "4px 8px",
                      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: "#cbd5e1" }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                      zIndex: 100
                    }),
                    option: (base, state) => ({
                      ...base,
                      padding: "10px 14px",
                      fontWeight: 600,
                      backgroundColor: state.isSelected ? "#eff6ff" : state.isFocused ? "#f8fafc" : "#fff",
                      color: state.isSelected ? "#1d4ed8" : "#1e293b",
                      borderLeft: state.isSelected ? "3px solid #3b82f6" : "3px solid transparent",
                      cursor: "pointer"
                    })
                  }}
                />
                {assignSupervisor && workersList.filter(w => w.worker_name !== selectedTicket?.original_worker_name).length === 0 && (
                  <div style={{ marginTop: "8px", padding: "10px 14px", backgroundColor: "#fef2f2", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: "8px", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center" }}>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    No eligible workers found. Please ask workers to punch in first!
                  </div>
                )}
              </div>

              {/* Qty Input with max limit */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", textTransform: "uppercase" }}>
                  <span><i className="bi bi-boxes me-2 text-success"></i> Quantity to Remediate (pcs) *</span>
                  <span style={{ background: "#fef2f2", color: "#dc2626", padding: "2px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, textTransform: "none" }}>
                    Max: {availableQty} pcs {selectedTicket?.ticket_type === 'QUALITY' ? '(rework)' : '(shortage)'}
                  </span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={assignQty}
                  disabled
                  readOnly
                  style={{ borderRadius: "12px", padding: "12px 16px", border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', fontWeight: 600, boxShadow: "none", color: "#64748b" }}
                />
              </div>

              {/* Standard Time & Due Time */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                    <i className="bi bi-clock me-2 text-warning"></i> Standard Time (mins)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={standardTime}
                    onChange={handleStandardTimeChange}
                    placeholder="e.g. 120"
                    min="1"
                    style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, boxShadow: "none" }}
                  />
                </div>
                <div className="col-md-6">
                  <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                    <i className="bi bi-calendar-event me-2 text-danger"></i> Due Time
                  </label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={dueTime}
                    onChange={handleDueTimeChange}
                    min={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                    style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, boxShadow: "none" }}
                  />
                </div>
              </div>

              {/* Verifier Selection */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-shield-check me-2 text-primary"></i> Verifier Selection
                </label>
                <select
                  className="form-select"
                  value={verifier}
                  onChange={e => setVerifier(e.target.value)}
                  style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, boxShadow: "none", cursor: "pointer" }}
                >
                  <option value="QC">QC</option>
                  <option value="Final Checker">Final Checker</option>
                  <option value="Manager">Manager</option>
                  <option value="Original Complainant">Original Complainant</option>
                </select>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "8px", display: "block", textTransform: "uppercase" }}>
                  <i className="bi bi-chat-text me-2 text-muted"></i> Notes (Optional)
                </label>
                <textarea
                  className="form-control"
                  value={assignNotes}
                  onChange={e => setAssignNotes(e.target.value)}
                  rows={3}
                  placeholder="Additional instructions for the worker..."
                  style={{ borderRadius: "12px", padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 500, boxShadow: "none", resize: "none" }}
                />
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <button
                  className="btn flex-grow-1"
                  onClick={() => setShowModal(false)}
                  style={{ background: "#f1f5f9", color: "#475569", borderRadius: "12px", padding: "14px", fontWeight: 700, border: "none" }}
                >
                  Cancel
                </button>
                <button
                  className="btn flex-grow-1"
                  onClick={handleAssignRemediation}
                  disabled={assigning}
                  style={{ background: "linear-gradient(135deg, #dc2626, #991b1b)", color: "#fff", borderRadius: "12px", padding: "14px", fontWeight: 800, border: "none", boxShadow: "0 4px 12px rgba(220,38,38,0.3)", transition: "all 0.2s" }}
                >
                  {assigning ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span> Assigning...</>
                  ) : (
                    <><i className="bi bi-person-check-fill me-2"></i> Assign Task</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MANPOWER ADJUSTMENT MODAL ── */}
      {mpModalType && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(6px)" }}>
          <div style={{ background: "#fff", borderRadius: "24px", width: "100%", maxWidth: "500px", display: "flex", flexDirection: "column", boxShadow: "0 25px 60px rgba(0,0,0,0.3)", animation: "slideUpModal 0.25s ease" }}>
            
            <div style={{ background: "#f8fafc", padding: "20px 24px", borderRadius: "24px 24px 0 0", borderBottom: "1px solid #e2e8f0" }}>
              <div className="d-flex justify-content-between align-items-center">
                <h4 style={{ margin: 0, fontWeight: 800, fontSize: "18px", color: "#0f172a" }}>
                  {mpModalType === 'BORROW' ? 'Borrow Worker' : mpModalType === 'OVERTIME' ? 'Assign Overtime' : 'Log Training'}
                </h4>
                <button onClick={() => setMpModalType(null)} style={{ background: "#f1f5f9", border: "none", width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", cursor: "pointer", transition: "all 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#e2e8f0"} onMouseOut={e => e.currentTarget.style.background = "#f1f5f9"}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <div style={{ padding: "24px" }}>
              <div className="mb-3">
                <label style={{ fontSize: "13px", fontWeight: 700, color: "#475569", marginBottom: "6px", display: "block" }}>Department needing workers</label>
                <Select
                  options={activeProcesses.map(p => ({ value: p.process_id, label: p.name }))}
                  value={mpRedProcess ? { value: mpRedProcess, label: activeProcesses.find(p => String(p.process_id) === String(mpRedProcess))?.name } : null}
                  onChange={handleRedProcessChange}
                  placeholder="-- Choose department --"
                  isClearable
                  styles={{ control: (base) => ({ ...base, borderRadius: "10px", border: "2px solid #e2e8f0", padding: "4px", fontWeight: 600, color: "#1e293b", boxShadow: "none" }) }}
                />
              </div>

              {mpModalType === 'BORROW' && (
                <div className="mb-3">
                  <label style={{ fontSize: "13px", fontWeight: 700, color: "#475569", marginBottom: "6px", display: "block" }}>Borrow worker from</label>
                  <Select
                    options={activeProcesses.map(p => ({ value: p.name, label: p.name }))}
                    value={mpSourceProcess ? { value: mpSourceProcess, label: mpSourceProcess } : null}
                    onChange={handleSourceProcessChange}
                    placeholder="-- Choose source department --"
                    isClearable
                    styles={{ control: (base) => ({ ...base, borderRadius: "10px", border: "2px solid #e2e8f0", padding: "4px", fontWeight: 600, color: "#1e293b", boxShadow: "none" }) }}
                  />
                </div>
              )}

              {mpModalType !== 'BORROW' && (
                <div className="mb-3 text-muted" style={{fontSize: "12px"}}>
                  <em>Department choose karne ke baad available workers neeche dikhenge.</em>
                </div>
              )}

              {(mpModalType === 'BORROW' ? mpSourceProcess : mpRedProcess) && (
                <div className="mb-3">
                  <label style={{ fontSize: "13px", fontWeight: 700, color: "#475569", marginBottom: "6px", display: "block" }}>Choose worker</label>
                  <Select
                    options={mpAvailableWorkers.map(w => ({ value: w.worker_id, label: `${w.worker_name} (${w.worker_id})` }))}
                    value={mpSelectedWorker ? { value: mpSelectedWorker, label: mpAvailableWorkers.find(w => String(w.worker_id) === String(mpSelectedWorker)) ? `${mpAvailableWorkers.find(w => String(w.worker_id) === String(mpSelectedWorker)).worker_name} (${mpSelectedWorker})` : mpSelectedWorker } : null}
                    onChange={(selectedOption) => setMpSelectedWorker(selectedOption ? selectedOption.value : '')}
                    placeholder="-- Choose worker --"
                    isClearable
                    styles={{ control: (base) => ({ ...base, borderRadius: "10px", border: "2px solid #e2e8f0", padding: "4px", fontWeight: 600, color: "#1e293b", boxShadow: "none" }) }}
                  />
                  {mpAvailableWorkers.length === 0 && (
                    <div className="text-danger mt-1" style={{fontSize: "12px"}}><i className="bi bi-info-circle me-1"></i> No workers found in this department.</div>
                  )}
                </div>
              )}
              {mpModalType === 'OVERTIME' && (
                <div className="row g-2 mb-3">
                  <div className="col-md-6"><label className="form-label small fw-bold">OT start time *</label><input type="datetime-local" className="form-control" value={otStartAt} onChange={e => setOtStartAt(e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-bold">OT end time *</label><input type="datetime-local" className="form-control" value={otEndAt} onChange={e => setOtEndAt(e.target.value)} /></div>
                  <div className="col-md-6"><label className="form-label small fw-bold">Total OT hours *</label><input type="number" min="0.5" step="0.5" className="form-control" placeholder="e.g. 2" value={otHours} onChange={e => setOtHours(e.target.value)} /></div>
                </div>
              )}
              {mpModalType === 'BORROW' && (
                <div className="mb-3"><label className="form-label small fw-bold">Lending supervisor approval *</label><input className="form-control" placeholder="Name / employee ID of lending supervisor" value={lendingSupervisor} onChange={e => setLendingSupervisor(e.target.value)} /><div className="form-text">Worker borrow record tabhi save hoga jab lending supervisor approval diya ho.</div></div>
              )}
              {mpModalType !== 'TRAIN' && (
                <>
                  <div className="mb-3"><label className="form-label small fw-bold">Expected additional capacity (pieces)</label><input type="number" min="1" className="form-control" placeholder="Expected pieces this action will recover" value={expectedCapacity} onChange={e => setExpectedCapacity(e.target.value)} /></div>
                  <div className="mb-3"><label className="form-label small fw-bold">Manager note</label><textarea rows="2" className="form-control" placeholder="Reason / capacity plan" value={manpowerNotes} onChange={e => setManpowerNotes(e.target.value)} /></div>
                </>
              )}
              {mpModalType === 'TRAIN' && (
                <>
                  <label className="form-label small fw-bold">Skill / process to train *</label><input className="form-control mb-2" placeholder="Skill / process to train" value={trainingSkill} onChange={e => setTrainingSkill(e.target.value)} />
                  <div className="row g-2 mb-2"><div className="col-md-6"><label className="form-label small fw-bold">Training start *</label><input type="datetime-local" className="form-control" value={trainingStartAt} onChange={e => setTrainingStartAt(e.target.value)} /></div><div className="col-md-6"><label className="form-label small fw-bold">Training end *</label><input type="datetime-local" className="form-control" value={trainingEndAt} onChange={e => setTrainingEndAt(e.target.value)} /></div></div>
                  <label className="form-label small fw-bold">Test / assessment result *</label><input className="form-control mb-2" placeholder="e.g. Practical test pending / Pass" value={trainingTestResult} onChange={e => setTrainingTestResult(e.target.value)} />
                  <label className="form-label small fw-bold">Independent production approver *</label><input className="form-control mb-2" placeholder="Approver name / employee ID" value={trainingApprover} onChange={e => setTrainingApprover(e.target.value)} />
                  <textarea className="form-control" placeholder="Training notes" value={trainingNotes} onChange={e => setTrainingNotes(e.target.value)} />
                </>
              )}
            </div>

            <div style={{ padding: "16px 24px", background: "#f8fafc", borderRadius: "0 0 24px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button onClick={() => setMpModalType(null)} className="btn" style={{ background: "#fff", border: "1px solid #cbd5e1", color: "#475569", fontWeight: 600, padding: "10px 20px", borderRadius: "10px" }}>Cancel</button>
              <button 
                onClick={submitManpowerAction} 
                disabled={submittingManpower || !mpRedProcess || !mpSelectedWorker || (mpModalType === 'TRAIN' && !trainingSkill)}
                className="btn" 
                style={{ background: mpModalType === 'BORROW' ? "#2563eb" : (mpModalType === 'OVERTIME' ? "#d97706" : "#dc2626"), color: "#fff", fontWeight: 600, padding: "10px 20px", borderRadius: "10px" }}
              >
                {submittingManpower ? 'Submitting...' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
          <div style={{ position: "relative", maxWidth: "90%", maxHeight: "90%" }}>
            <button onClick={() => setPreviewImage(null)} style={{ position: "absolute", top: "-40px", right: "0", background: "none", border: "none", color: "#fff", fontSize: "30px", cursor: "pointer" }}>
              <i className="bi bi-x-circle-fill"></i>
            </button>
            <img src={previewImage} alt="Preview" style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: "8px", objectFit: "contain", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUpModal {
          from { opacity: 0; transform: translateY(30px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .form-select:focus, .form-control:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        .report-table thead th { color: #ffffff !important; }
        /* Custom scrollbar for horizontal scrolling image containers */
        .overflow-auto::-webkit-scrollbar {
          height: 6px;
        }
        .overflow-auto::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}
